import { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { API, api } from "@/api";
import { pick } from "@/i18n";
import { GoldImage } from "../GoldImage";
import { Stars, absPhoto, eur } from "./utils";

/* Sostituzione servizio: SOLO alternative reali dal catalogo esistente
 * (alloggi della città, esperienze, transfer del gestionale). */
export const SwapDialog = ({ kind, current, cityId, onSelect, onClose, tc, lang }) => {
    const [items, setItems] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        let alive = true;
        setItems(null);
        const load = async () => {
            try {
                let rows = [];
                if (kind === "stay") {
                    const r = await api.get(`/public/stays/${cityId || "roma"}`);
                    rows = (r.data.stays || []).map((s) => ({
                        ref_id: s.place_id, city_id: cityId || "roma", name: s.name,
                        sub: s.address, rating: s.rating, reviews: s.reviews, price: null,
                        photo: `${API}/public/stays/${cityId || "roma"}/${s.place_id}/photo`,
                    }));
                } else if (kind === "experience") {
                    const r = await api.get("/public/experiences");
                    rows = (r.data.experiences || []).map((e) => ({
                        ref_id: e.id, name: pick(e.name, lang),
                        sub: [e.locality, e.region].filter(Boolean).join(" · "),
                        rating: e.rating, price: null,
                        photo: `${API}/public/experiences/${e.id}/photo`,
                    }));
                } else if (kind === "transfer") {
                    const r = await api.get("/public/transfers");
                    const groups = r.data.groups || [];
                    rows = groups.flatMap((g) => (g.items || []).map((it) => ({
                        ref_id: it.id, name: pick(it.name, lang),
                        sub: `${pick(it.from, lang)} → ${pick(it.to, lang)}`,
                        price: it.price, price_type: it.priceType,
                        photo: it.image_url ? absPhoto(it.image_url) : "",
                    })));
                } else if (kind === "restaurant") {
                    const r = await api.get("/public/restaurants", { params: cityId ? { city: cityId } : {} });
                    rows = (r.data.restaurants || []).map((x) => ({
                        ref_id: x.id, city_id: x.city_id, name: x.name,
                        sub: [pick(x.type_label, lang), x.address].filter(Boolean).join(" · "),
                        rating: x.rating, reviews: x.reviews, price_level: x.price_level, price: null,
                        photo: `${API}/public/restaurants/${x.id}/photo`,
                    }));
                }
                if (alive) setItems(rows.filter((x) => x.ref_id !== current?.ref_id));
            } catch {
                if (alive) setItems([]);
            }
        };
        load();
        return () => { alive = false; };
    }, [kind, cityId, current, lang]);

    const filtered = (items || []).filter((x) => !q || `${x.name} ${x.sub}`.toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="swap-dialog" onClick={onClose}>
            <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl border border-amber-500/30 bg-[#101012] p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="font-display text-lg text-amber-50">{tc.swapTitle}</h3>
                        <p className="mt-0.5 truncate text-xs text-stone-500">{svcCurrent(current, lang)}</p>
                    </div>
                    <button onClick={onClose} data-testid="swap-close" aria-label={tc.close} className="text-stone-500 transition-colors hover:text-amber-300"><X size={18} /></button>
                </div>
                <div className="relative mt-4">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-500" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tc.swapSearch} data-testid="swap-search"
                        className="input-lux w-full !ps-9" />
                </div>
                <div className="mt-4 flex-1 space-y-2 overflow-y-auto pe-1" data-testid="swap-list">
                    {items === null ? (
                        <p className="flex items-center justify-center gap-2 py-10 text-xs text-stone-500"><Loader2 size={14} className="animate-spin" /> {tc.swapLoading}</p>
                    ) : filtered.length === 0 ? (
                        <p className="py-10 text-center text-xs italic text-stone-500" data-testid="swap-empty">{tc.swapEmpty}</p>
                    ) : (
                        filtered.map((x) => (
                            <div key={x.ref_id} className="flex items-center gap-3 rounded-xl border border-amber-500/15 bg-[#0c0c0e] p-2.5 transition-colors hover:border-amber-400/40" data-testid={`swap-item-${x.ref_id}`}>
                                <GoldImage src={x.photo} alt={x.name} className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-amber-50">{x.name}</p>
                                    <p className="truncate text-[10px] text-stone-500">{x.sub}</p>
                                    <Stars rating={x.rating} reviews={x.reviews} />
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1.5">
                                    <span className={`text-[10px] font-semibold ${x.price != null || x.price_level ? "text-amber-300" : "text-stone-500"}`}>
                                        {x.price != null ? eur(x.price) : (x.price_level || tc.priceQuote)}
                                    </span>
                                    <button onClick={() => onSelect(x)} data-testid={`swap-select-${x.ref_id}`}
                                        className="rounded-full bg-amber-400 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-black transition-colors hover:bg-amber-300">
                                        {tc.swapSelect}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const svcCurrent = (item, lang) => pick(item?.name, lang);
